import { useEffect, useState } from "react";
import {
  COMMISSION_LEVEL_OPTIONS,
  compensationTypeLabel,
  REFERRAL_CODE_KIND_DETAILS,
  REFERRAL_CODE_KINDS,
  referralKindCarriesCommission,
  selectableCommissionLevels,
  type ReferralCodeKind,
} from "@aam/shared";
import { getSessionToken, useSession } from "../hooks/useSession";
import { useToast } from "../context/ToastContext";
import { cn } from "../lib/cn";
import { Card } from "./ui/Card";
import { Button, Label, LoadingStatus, Select, Skeleton } from "./ui";
import { CompensationTypeSelect } from "./CompensationTypeSelect";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface ReferralCodeRow {
  id: string;
  code: string;
  active: boolean;
  useCount: number;
  maxUses: number | null;
  expiresAt: string | null;
  createdAt: string;
  kind: ReferralCodeKind;
  commissionLevelAtCreation: string | null;
  compensationTypeAtCreation: string | null;
}

export interface ReferralOwnerOption {
  /** Portal user id, or managed agent id for the requester themselves. */
  id: string;
  label: string;
  commissionLevel: string | null;
}

interface ReferralLinksPanelProps {
  ownerUserId?: string;
  /** Staff creating links for a managed agent vs an agent's own downline links */
  context?: "self" | "managed-agent";
  /**
   * Who the new agent joins under. When given, the panel offers a picker —
   * the first option is the default, and is expected to be the requester.
   */
  ownerOptions?: ReferralOwnerOption[];
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  }
}

export function ReferralLinksPanel({
  ownerUserId,
  context = "self",
  ownerOptions = [],
}: ReferralLinksPanelProps) {
  const token = getSessionToken();
  const { agent } = useSession();
  const { success, error: toastError } = useToast();
  // RingIQ creates dialer-only accounts, so only staff choose an invite type;
  // an agent's own downline link is always a standard contracted invite.
  const canSelectInviteType = Boolean(agent?.isStaff);
  const [code, setCode] = useState<ReferralCodeRow | null>(null);
  const [ownerLevel, setOwnerLevel] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [compensationType, setCompensationType] = useState("");
  const [loadingLevel, setLoadingLevel] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [kind, setKind] = useState<ReferralCodeKind>("STANDARD");
  const carriesCommission = referralKindCarriesCommission(kind);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");
  // The picker wins when one is offered; otherwise the panel owns one agent.
  const activeOwnerId = selectedOwnerId || ownerOptions[0]?.id || ownerUserId;
  const activeOwner = ownerOptions.find((option) => option.id === activeOwnerId) ?? null;
  const ownsForSomeoneElse = Boolean(
    activeOwner && ownerOptions[0] && activeOwner.id !== ownerOptions[0].id
  );

  const selectableLevels = selectableCommissionLevels(
    COMMISSION_LEVEL_OPTIONS.map((option) => option.value),
    ownerLevel
  );

  useEffect(() => {
    setCode(null);
    setCopied(false);
    if (!token || !activeOwnerId) {
      setLoadingLevel(false);
      return;
    }
    // A downline's level is already on the row that opened this panel; the
    // lookup only sharpens it, so a 404 for a contractless agent is harmless.
    const knownLevel = activeOwner?.commissionLevel ?? null;
    let cancelled = false;
    setLoadingLevel(true);
    setError(null);
    void fetch(`${API_BASE}/api/agents/${encodeURIComponent(activeOwnerId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) return null;
        const json = await response.json();
        return json.success ? (json.data.commissionLevel as string | null) : null;
      })
      // A missing/unresolvable profile (e.g. no submission yet) just means we
      // can't cap the level — fall back to offering every level.
      .catch(() => null)
      .then((level) => {
        if (cancelled) return;
        setOwnerLevel(level ?? knownLevel);
      })
      .finally(() => {
        if (!cancelled) setLoadingLevel(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activeOwnerId]);

  useEffect(() => {
    if (selectableLevels.length === 0) {
      setSelectedLevel("");
      return;
    }
    setSelectedLevel((current) => (selectableLevels.includes(current) ? current : selectableLevels[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerLevel]);

  async function generateLink() {
    if (!token || !activeOwnerId) return;
    if (carriesCommission && (!selectedLevel || !compensationType)) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/referral-codes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ownerUserId: activeOwnerId,
          kind,
          ...(carriesCommission ? { commissionLevel: selectedLevel, compensationType } : {}),
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Failed to create registration link");
      setCode(json.data as ReferralCodeRow);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create registration link");
    } finally {
      setGenerating(false);
    }
  }

  const registrationUrl = code ? `${window.location.origin}/join/${code.code}` : null;

  async function handleCopy() {
    if (!registrationUrl) return;
    const didCopy = await copyToClipboard(registrationUrl);
    if (didCopy) {
      setCopied(true);
      success("Copied");
      window.setTimeout(() => setCopied(false), 2000);
      return;
    }
    toastError("Could not copy link");
  }

  return (
    <Card padding="lg" className="relative min-h-[250px] space-y-5 overflow-hidden border-2 bg-status-nigo-bg border-status-nigo-border">
      <div className="pointer-events-none absolute -bottom-10 -right-8 h-40 w-40 text-status-nigo-text opacity-10" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2m7-10a4 4 0 100-8 4 4 0 000 8zm8 1v6m3-3h-6" /></svg>
      </div>
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-status-nigo-text">Grow your team</p>
        <h2 className="mt-2 font-display text-2xl font-extrabold text-content-primary">
          {context === "managed-agent" ? "Registration link" : "Downline registration link"}
        </h2>
        <p className="mt-1 text-sm text-content-secondary">
          {REFERRAL_CODE_KIND_DETAILS[kind].description}
        </p>
        {ownsForSomeoneElse && activeOwner && (
          <p className="mt-2 text-sm font-semibold text-content-primary">
            This link puts the new agent under {activeOwner.label}, not you.
          </p>
        )}
      </div>

      {error && <p role="alert" className="rounded-card border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">{error}</p>}

      {loadingLevel ? (
        <div className="space-y-3" role="status" aria-busy="true">
          <LoadingStatus label="Loading commission level" />
          <Skeleton className="h-16 w-full rounded-card" />
          <Skeleton className="h-5 w-36" />
        </div>
      ) : registrationUrl && code ? (
        <div className="relative z-10 rounded-card border-2 border-border-default bg-surface-raised p-4">
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <p className="flex-1 break-all font-mono text-sm text-content-primary">
              {registrationUrl}
            </p>
            <Button
              type="button"
              onClick={handleCopy}
              variant={copied ? "primary" : "secondary"}
              className={cn("shrink-0", copied && "border-status-active-border bg-status-active-bg text-status-active-text")}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-content-muted">
              {REFERRAL_CODE_KIND_DETAILS[code.kind ?? "STANDARD"].label}
              {code.kind === "RING_IQ" ? "" : ` • Commission level: ${code.commissionLevelAtCreation ?? "—"}`}
              {code.kind === "RING_IQ" ? "" : ` • ${compensationTypeLabel(code.compensationTypeAtCreation) ?? "Advance or as earned not set"}`}
              {" • "}Uses: {code.useCount}
              {code.maxUses ? ` / ${code.maxUses}` : ""} • {code.active ? "Active" : "Inactive"}
            </p>
            <button
              type="button"
              className="text-xs font-semibold text-accent hover:opacity-90"
              onClick={() => setCode(null)}
            >
              Create a different link
            </button>
          </div>
        </div>
      ) : (
        <div className="relative z-10 flex flex-col gap-3 rounded-card border-2 border-border-default bg-surface-raised p-4 sm:flex-row sm:items-end">
          {ownerOptions.length > 1 && (
            <Label className="flex-1">
              Joins under
              <Select
                className="mt-1.5"
                value={activeOwnerId ?? ""}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
              >
                {ownerOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Label>
          )}
          {canSelectInviteType && (
            <Label className="flex-1">
              Invite type
              <Select
                className="mt-1.5"
                value={kind}
                onChange={(e) => setKind(e.target.value as ReferralCodeKind)}
              >
                {REFERRAL_CODE_KINDS.map((option) => (
                  <option key={option} value={option}>
                    {REFERRAL_CODE_KIND_DETAILS[option].label}
                  </option>
                ))}
              </Select>
            </Label>
          )}
          {/* A RingIQ link is dialer-only, so there's no level to pick. */}
          {carriesCommission && (
            <Label className="flex-1">
              Commission level
              <Select
                className="mt-1.5"
                value={selectedLevel}
                disabled={selectableLevels.length === 0}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                {selectableLevels.map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Select>
              <span className="mt-1 block text-xs text-content-muted">
                {ownerLevel
                  ? ownsForSomeoneElse && activeOwner
                    ? `Capped at ${activeOwner.label}'s level (${ownerLevel})`
                    : `Capped at your own level (${ownerLevel})`
                  : "No level on file yet — any level is selectable"}
              </span>
            </Label>
          )}
          {carriesCommission && (
            <CompensationTypeSelect
              className="flex-1"
              value={compensationType}
              onChange={setCompensationType}
            />
          )}
          <Button
            type="button"
            onClick={() => void generateLink()}
            disabled={generating || (carriesCommission && (!selectedLevel || !compensationType))}
            className="shrink-0"
          >
            {generating ? "Generating…" : "Generate link"}
          </Button>
        </div>
      )}
    </Card>
  );
}
